/*
 * Claim Value Type area (audit Phase 3.3). Replaces wwwroot/js/Claim/ValueType.js
 * and wwwroot/js/Claim/ExternalValues.js with one module on the Phase 3.1 core.
 * No jQuery.
 *
 * A Claim Value Type is the vocabulary a claim's VALUE is drawn from. It has two
 * editors, because the two kinds of value type are addressed differently:
 *
 *   ValueType    the domain's own types, which have a row and an Id. Edited in
 *                the Value Type modal: Properties on one tab, the defined values
 *                on the other, each value saved individually.
 *   ExternalValues  the Microsoft standard types, which come from a hardcoded
 *                list (ClaimValueTypeRepository.GetStandardValueTypes) and have
 *                no Id at all, so the id-keyed Values tab cannot reach them.
 *                Their own page is keyed by the value-type STRING and posts the
 *                whole list as one indexed form.
 *
 * Defects fixed in the move:
 *  - Both modal openers passed formId 'ClaimValueTypeForm'. No view renders a
 *    form by that name -- they are EditValueTypeForm and CreateValueTypeForm --
 *    so validation was never attached to either, and the isValid() gate in front
 *    of both saves passed unconditionally. Required fields reached the Api and
 *    came back as a server-side rejection instead of an inline message.
 *  - Delete's completion handler used find('fa-spinner') and find('fa-trash'):
 *    element selectors, matching <fa-spinner> and <fa-trash> tags that do not
 *    exist. The row's spinner was shown and never hidden again, and the trash
 *    icon stayed hidden after a refused delete, so the row could not be retried
 *    without a reload.
 *  - Edit's onSuccess clicked the Properties tab to make the pane visible,
 *    because the panes carried Bootstrap 3's `fade in active` and BS5 needs
 *    `fade show active`. The markup is fixed and the strip carries
 *    data-mcp-autotab, so no handler has to open a tab by hand.
 *  - EditSave pointed its busy indicator at '#EditClaimValueTypeContainer
 *    .fa-spinner'; the Edit modal's footer rendered no spinner, so the save gave
 *    no feedback at all. The markup has one now.
 *  - Neither save wrote to #EditValueTypeMessageContainer or
 *    #CreateValueTypeMessageContainer, so both alert hosts were dead markup.
 */
import { ajaxHtml, ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, closest, one } from '../core/dom';
import { serialize } from '../core/forms';
import { modelStateSummary } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'ValueTypeModal';

function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

function rowSelector(valueTypeId: number | string): string {
    return `.claimvaluetype-row[data-id="${valueTypeId}"]`;
}

interface SaveTypeOptions {
    formId: string;
    container: string;
    method: 'POST' | 'PUT';
    alertInto: string;
    errorTitle: string;
    onSaved: (data: unknown) => void;
}

function saveValueType(options: SaveTypeOptions): void {
    const form = one(`#${options.formId}`) as HTMLFormElement | null;
    if (!form || !isValid(form)) return;
    setHidden(`#${options.container} .fa-save`, true);
    void ajaxJson({
        url: 'ClaimValueType',
        method: options.method,
        data: serialize(form),
        busy: `#${options.container} .fa-spinner`,
        alertInto: options.alertInto,
        errorTitle: options.errorTitle,
        onSuccess: data => {
            hideModal(MODAL);
            options.onSaved(data);
        },
        onFailure: data => {
            const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
            if (modelState) setValidationModelState(modelState);
            return true;
        },
        onError: (_xhr, _status, error) => {
            toast('danger', options.errorTitle, error);
            return true;
        },
        onComplete: () => setHidden(`#${options.container} .fa-save`, false),
    });
}

export const ValueType = {
    Edit(valueTypeId: number | string): void {
        const row = rowSelector(valueTypeId);
        setHidden(`${row} .fa-edit`, true);
        void ajaxModal({
            modal: MODAL,
            url: `ClaimValueType/${valueTypeId}/Edit`,
            formId: 'EditValueTypeForm',
            focusId: 'ClaimValueType_Name',
            busy: `${row} .fa-spinner`,
            onError: () => {
                toast('danger', `Failed to load Claim Value Type ${valueTypeId} for editing`);
                return true;
            },
            onComplete: () => setHidden(`${row} .fa-edit`, false),
        });
    },

    EditCancel(): void {
        hideModal(MODAL);
    },

    EditSave(): void {
        const valueTypeId = (document.getElementById('ClaimValueType_Id') as HTMLInputElement | null)?.value;
        saveValueType({
            formId: 'EditValueTypeForm',
            container: 'EditClaimValueTypeContainer',
            method: 'PUT',
            alertInto: 'EditValueTypeMessageContainer',
            errorTitle: 'Failed to save Value Type changes',
            onSaved: () => {
                if (valueTypeId) void ValueType.ReloadRow(valueTypeId);
            },
        });
    },

    Create(domainId: number | string): void {
        setHidden('#NewClaimValueTypeButton', true);
        void ajaxModal({
            modal: MODAL,
            url: `ClaimValueType/Domain/${domainId}/Create`,
            formId: 'CreateValueTypeForm',
            focusId: 'ClaimValueType_Name',
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to render new Claim Value Type entry form', error);
                return true;
            },
            onComplete: () => setHidden('#NewClaimValueTypeButton', false),
        });
    },

    CreateCancel(): void {
        hideModal(MODAL);
    },

    CreateSave(): void {
        saveValueType({
            formId: 'CreateValueTypeForm',
            container: 'CreateClaimValueTypeContainer',
            method: 'POST',
            alertInto: 'CreateValueTypeMessageContainer',
            errorTitle: 'Failed to save Claim Value Type',
            onSaved: data => {
                const id = (data as { data?: { id?: number } } | null)?.data?.id;
                if (!id) {
                    toast('warning', 'Value Type saved', 'Reload the list to see it.');
                    return;
                }
                // Park a placeholder row so ReloadRow has something to replace.
                const body = document.getElementById('ClaimValueTypeContainer');
                if (body) {
                    const row = document.createElement('tr');
                    row.className = 'claimvaluetype-row';
                    row.setAttribute('data-id', String(id));
                    row.innerHTML = '<td colspan="5"><i class="fas fa-spinner fa-spin"></i></td>';
                    body.appendChild(row);
                }
                void ValueType.ReloadRow(id);
            },
        });
    },

    ReloadRow(valueTypeId: number | string): Promise<void> {
        const row = rowSelector(valueTypeId);
        setHidden(`${row} .fa-edit`, true);
        return ajaxSwap({
            container: row,
            url: `ClaimValueType/${valueTypeId}`,
            mode: 'replaceWith',
            busy: `${row} .fa-spinner`,
            onError: () => {
                toast('danger', 'Failed to load Claim Value Type');
                return true;
            },
            // After replaceWith the original node is detached, but the re-rendered
            // element matches the same selector -- re-query for the icon state.
            onComplete: () => setHidden(`${row} .fa-edit`, false),
        });
    },

    Delete(valueTypeId: number | string): void {
        const row = rowSelector(valueTypeId);
        const name = one(`${row} .valuetype-name`)?.textContent?.trim() ?? '';
        void confirm({
            title: 'Delete Claim Value Type',
            message: name ? `Delete claim value type "${name}"?` : 'Delete this claim value type?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                setHidden(`${row} .fa-trash`, true);
                void ajaxJson({
                    url: `ClaimValueType/${valueTypeId}`,
                    method: 'DELETE',
                    busy: `${row} .fa-spinner`,
                    onSuccess: () => {
                        for (const node of all(row)) node.remove();
                    },
                    onFailure: data => {
                        toast('danger', `Failed to delete ${name || 'the'} value type`, summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', `Failed to delete ${name || 'the'} value type`, error);
                        return true;
                    },
                    // Class selectors. The legacy pair looked for <fa-trash> and
                    // <fa-spinner> ELEMENTS, so a refused delete left the row's
                    // spinner running and its trash icon hidden for good.
                    onComplete: () => setHidden(`${row} .fa-trash`, false),
                });
            },
        });
    },

    // ---------------------------------------------------------------------
    // Defined values (the Values tab of the Value Type modal)
    //
    // A row is either a display row or an edit row. Editing keeps the display row
    // in the DOM (hidden) with the edit row inserted right after it, so Cancel is
    // a local operation -- no re-fetch, and nothing to lose if the server is
    // unreachable. The unsaved add-row is the one edit row with no display row
    // behind it; it is marked data-id="0" and Cancel just drops it.
    //
    // Only ever one editor open: the row partials use asp-for, so two open editors
    // would put duplicate element ids in the document.
    // ---------------------------------------------------------------------

    NewValue(): void {
        closeValueEditors();
        void ajaxSwap({
            container: '#ClaimValuesContainer',
            url: `ClaimValueType/${valueTypeIdForValues()}/Value/New`,
            mode: 'append',
            busy: '#ClaimValuesLoading',
            onSuccess: () => focusEditor(),
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to add a value row', error);
                return true;   // else the alert replaces the whole value list
            },
        });
    },

    EditValue(valueId: number | string): void {
        closeValueEditors();
        const row = valueRowSelector(valueId);
        setHidden(`${row} .fa-edit`, true);
        void ajaxSwap({
            container: row,
            url: `ClaimValueType/Value/${valueId}/Edit`,
            mode: 'after',
            busy: `${row} .fa-spinner`,
            onSuccess: () => {
                for (const node of all(row)) node.classList.add('d-none');
                focusEditor();
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to load the value for editing', error);
                return true;
            },
            onComplete: () => setHidden(`${row} .fa-edit`, false),
        });
    },

    CancelValue(valueId: number | string): void {
        for (const node of all(`${VALUE_EDIT_SELECTOR}[data-id="${valueId}"]`)) node.remove();
        if (Number(valueId) > 0) {
            for (const node of all(valueRowSelector(valueId))) node.classList.remove('d-none');
        }
    },

    /**
     * A rename is the quiet hazard here: it reads as fixing a label, but every
     * stored claim keys off the value string, so changing it orphans all of them
     * at once. When the value itself changed, check what carries it before saving
     * and offer to bring those claims along. Editing only the Name goes straight
     * through.
     */
    SaveValue(valueId: number | string): void {
        const editSelector = `${VALUE_EDIT_SELECTOR}[data-id="${valueId}"]`;
        const edit = one(editSelector);
        if (!edit) return;

        const original = edit.getAttribute('data-original-value');
        const proposed = (edit.querySelector('.field-value') as HTMLInputElement | null)?.value ?? '';
        const renamed = Number(valueId) > 0
            && original !== null
            && String(original) !== String(proposed)
            && proposed.length > 0;

        if (!renamed) {
            performSaveValue(valueId, null);
            return;
        }
        void ajaxHtml({
            url: `ClaimValueType/Value/${valueId}/Impact`,
            data: { mode: 'rename', newValue: proposed },
            busy: `${editSelector} .fa-spinner`,
            alertTitle: 'Failed to check what uses this value',
            onSuccess: html => {
                void confirm({
                    title: 'Rename Value',
                    html,
                    confirmText: 'Save',
                    variant: 'warning',
                    onConfirm: () => performSaveValue(valueId, readImpactChoice()),
                });
            },
        });
    },

    /**
     * Retiring a value never revokes the claims that carry it -- that is
     * deliberate, and an application may well depend on it. What it must not do is
     * happen silently, so the confirmation is built from a server-side impact
     * check rather than a blind "are you sure".
     */
    DeleteValue(valueId: number | string): void {
        void ajaxHtml({
            url: `ClaimValueType/Value/${valueId}/Impact`,
            busy: `${valueRowSelector(valueId)} .fa-spinner`,
            alertTitle: 'Failed to check what uses this value',
            onSuccess: html => {
                void confirm({
                    title: 'Delete Value',
                    html,
                    confirmText: 'Delete',
                    variant: 'danger',
                    onConfirm: () => {
                        applyRemap(readImpactChoice(), () => performDeleteValue(valueId));
                    },
                });
            },
        });
    },
};

const VALUE_EDIT_SELECTOR = '#ClaimValuesContainer .claimvaluetype-value-edit';

function valueRowSelector(valueId: number | string): string {
    return `#ClaimValuesContainer .claimvaluetype-value-row[data-id="${valueId}"]:not(.claimvaluetype-value-edit)`;
}

function valueTypeIdForValues(): string {
    return document.getElementById('ClaimValuesContainer')?.getAttribute('data-valuetype-id') ?? '';
}

function focusEditor(): void {
    (one(`${VALUE_EDIT_SELECTOR} .field-name`) as HTMLInputElement | null)?.focus();
}

function closeValueEditors(): void {
    for (const editor of all(VALUE_EDIT_SELECTOR)) {
        const editedId = Number(editor.getAttribute('data-id') ?? '0');
        editor.remove();
        if (editedId > 0) {
            for (const node of all(valueRowSelector(editedId))) node.classList.remove('d-none');
        }
    }
}

function reloadValues(): void {
    void ajaxSwap({
        container: '#ValueTypeValuesTab',
        url: `ClaimValueType/${valueTypeIdForValues()}/Values`,
        busy: '#ClaimValuesLoading',
        alertTitle: 'Failed to reload the value list',
    });
}

interface ImpactChoice {
    remap: boolean;
    valueType: string;
    fromValue: string;
    toValue: string;
    domainId: number;
    rows: unknown[];
}

/**
 * Reads the operator's choice out of the impact dialog. Called after the modal
 * has hidden -- the shared confirm modal keeps its body until the next confirm()
 * call, so the inputs are still in the DOM.
 *
 * getAttribute, never a jQuery-style typed read: a vocabulary of numeric values
 * ("1", "2", "3") would come back as numbers and the Api rejects a body binding
 * those onto string properties. The value is operator-authored -- it is not safe
 * to assume it looks like a word.
 */
function readImpactChoice(): ImpactChoice | null {
    const choice = document.getElementById('ValueImpactChoice');
    if (!choice) return null;

    let rows: unknown[] = [];
    const raw = (document.getElementById('ValueImpactRows') as HTMLInputElement | null)?.value;
    if (raw) {
        try {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) rows = parsed;
        } catch {
            rows = [];
        }
    }
    const action = one('#ValueImpactAction, input[name="ValueImpactAction"]:checked') as HTMLInputElement | null;
    return {
        remap: action?.value === 'remap',
        valueType: choice.getAttribute('data-value-type') ?? '',
        fromValue: choice.getAttribute('data-from-value') ?? '',
        toValue: (document.getElementById('ValueImpactReplacement') as HTMLInputElement | null)?.value ?? '',
        domainId: parseInt(choice.getAttribute('data-domain-id') ?? '', 10) || 0,
        rows,
    };
}

/**
 * Posts the re-map, then runs `next` regardless of the outcome. The re-map is
 * advisory cleanup attached to the operator's real action (the delete or the
 * rename); a failure to move the claims must not swallow the action itself, so it
 * reports and continues rather than aborting.
 */
function applyRemap(choice: ImpactChoice | null, next: () => void): void {
    if (!choice || !choice.remap || !choice.toValue || choice.rows.length === 0) {
        next();
        return;
    }
    void ajaxJson({
        url: 'ClaimValueType/Value/Remap',
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
            valueType: choice.valueType,
            fromValue: choice.fromValue,
            toValue: choice.toValue,
            domainId: choice.domainId,
            rows: choice.rows,
        }),
        onSuccess: data => {
            const result = (data as { data?: { applied?: number; messages?: string[] } } | null)?.data;
            const applied = result?.applied ?? 0;
            const messages = result?.messages ?? [];
            if (applied > 0) {
                toast('success', `Moved ${applied} claim${applied === 1 ? '' : 's'}`, messages.join(' '));
            } else if (messages.length > 0) {
                toast('warning', 'No claims were moved', messages.join(' '));
            }
            next();
        },
        onFailure: data => {
            toast('danger', 'Failed to move the claims', summaryOf(data));
            next();
            return true;
        },
        onError: (_xhr, _status, error) => {
            toast('danger', 'Failed to move the claims', error);
            next();
            return true;
        },
    });
}

/**
 * `choice`, when present, is the re-map the operator approved in the rename
 * dialog. It runs after the save, not before: the replacement has to be a defined
 * value by the time the re-map is validated, and after a rename that is this very
 * row.
 */
function performSaveValue(valueId: number | string, choice: ImpactChoice | null): void {
    const editSelector = `${VALUE_EDIT_SELECTOR}[data-id="${valueId}"]`;
    const edit = one(editSelector);
    if (!edit) return;

    const valueTypeId = valueTypeIdForValues();
    const isNew = !(Number(valueId) > 0);

    setHidden(`${editSelector} .fa-save`, true);
    void ajaxJson({
        url: isNew
            ? `ClaimValueType/${valueTypeId}/Value`
            : `ClaimValueType/${valueTypeId}/Value/${valueId}`,
        method: isNew ? 'POST' : 'PUT',
        // The row isn't inside a form -- the modal's only form is the Properties
        // tab -- so serialize the row's own inputs.
        data: serialize(edit),
        busy: `${editSelector} .fa-spinner`,
        onSuccess: data => {
            const savedId = isNew ? (data as { data?: { id?: number } } | null)?.data?.id : valueId;
            applyRemap(choice, () => {
                if (!savedId) {
                    // Saved, but we can't identify the row to render. Reload the tab.
                    reloadValues();
                    return;
                }
                showSavedValue(savedId, isNew, editSelector);
            });
        },
        onFailure: data => {
            // Toast rather than the inline validation spans: the row has none
            // (nothing client-side validates it), and the modal's only validation
            // summary lives in the other tab.
            toast('danger', isNew ? 'Failed to add the value' : 'Failed to save the value', summaryOf(data));
            return true;
        },
        onError: (_xhr, _status, error) => {
            toast('danger', 'Failed to save the value', error);
            return true;
        },
        onComplete: () => setHidden(`${editSelector} .fa-save`, false),
    });
}

/**
 * Renders the saved value as a display row and retires the editor. A new value is
 * appended; an edited one replaces the hidden row it came from.
 */
function showSavedValue(valueId: number | string, isNew: boolean, editSelector: string): void {
    void ajaxSwap({
        container: isNew ? '#ClaimValuesContainer' : valueRowSelector(valueId),
        url: `ClaimValueType/Value/${valueId}`,
        mode: isNew ? 'append' : 'replaceWith',
        onSuccess: () => {
            for (const node of all(editSelector)) node.remove();
        },
        onError: () => {
            // The save itself succeeded; only the re-render failed. Reload the list
            // rather than leaving a stale or duplicated row.
            reloadValues();
            return true;
        },
    });
}

function performDeleteValue(valueId: number | string): void {
    const row = valueRowSelector(valueId);
    setHidden(`${row} .fa-trash`, true);
    void ajaxJson({
        url: `ClaimValueType/Value/${valueId}`,
        method: 'DELETE',
        busy: `${row} .fa-spinner`,
        onSuccess: () => {
            ValueType.CancelValue(valueId);   // drop any open editor for the row
            for (const node of all(row)) node.remove();
        },
        onFailure: data => {
            toast('danger', 'Failed to delete the value', summaryOf(data));
            return true;
        },
        onError: (_xhr, _status, error) => {
            toast('danger', 'Failed to delete the value', error);
            return true;
        },
        onComplete: () => setHidden(`${row} .fa-trash`, false),
    });
}

// ---------------------------------------------------------------------------
// External value-type value editor (ClaimValueType/EditExternalValueTypes).
//
// Index discipline: ClaimValues[i] indices must stay contiguous from 0 or MVC's
// collection binder stops at the first gap and silently drops every row after it.
// So nothing is ever removed from the DOM -- a discarded new row is blanked and
// hidden, and the server skips blank rows that carry no Id. Renumbering on
// remove, or the ClaimValues.Index hidden-field trick, would both work; this
// needs neither.
// ---------------------------------------------------------------------------
export const ExternalValues = {
    /**
     * Appends a blank row from the <template>, substituting the next index for the
     * __i__ placeholder. The button is type="button" so it cannot submit the form
     * it lives in; the `false` return is a second guard.
     */
    AddRow(): false {
        const body = document.getElementById('ClaimValuesContainer');
        const template = document.getElementById('NewValueRowTemplate');
        if (!body || !(template instanceof HTMLTemplateElement)) return false;

        const index = parseInt(body.getAttribute('data-next-index') ?? '', 10) || 0;
        body.setAttribute('data-next-index', String(index + 1));

        // The <template> is cloned rather than re-parsed from innerHTML so a bare
        // <tr> is not auto-promoted out of the table it is being appended to.
        const fragment = template.content.cloneNode(true) as DocumentFragment;
        for (const node of fragment.querySelectorAll<HTMLElement>('[name*="__i__"]')) {
            node.setAttribute('name', (node.getAttribute('name') ?? '').split('__i__').join(String(index)));
        }
        const row = fragment.querySelector('tr');
        body.appendChild(fragment);
        (row?.querySelector('.field-name') as HTMLInputElement | null)?.focus();
        return false;
    },

    /**
     * One handler for both row kinds, deciding from the row itself rather than
     * from an index baked into a selector -- an index-based selector goes stale
     * the moment anything about the row order changes.
     *
     *   saved row (Id > 0)  -> hide + flip the Deleted flag; the POST deletes it
     *   new row  (no Id)    -> hide + blank the fields; the POST skips it
     *
     * Blanking matters: Name and Value are [Required], so a hidden-but-filled row
     * the operator meant to discard would fail validation on save with an error
     * pointing at a row they cannot see.
     */
    RemoveRow(this: Element): false {
        const row = closest<HTMLTableRowElement>(this, 'tr');
        if (!row) return false;
        const id = parseInt((row.querySelector('.field-valueid') as HTMLInputElement | null)?.value ?? '', 10) || 0;

        if (id > 0) {
            const deleted = row.querySelector('.field-deleted') as HTMLInputElement | null;
            if (deleted) deleted.value = 'true';
        } else {
            for (const field of row.querySelectorAll<HTMLInputElement>('.field-name, .field-value')) field.value = '';
        }
        row.classList.add('d-none');
        return false;
    },
};
